import * as React from 'react'
import { Table, Select, message } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { BMConfigStore, LocationMetaDataStore } from './BirchmountConfigStore'
import { observer, inject } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { ButtonGroup } from '../../../../../components/Button/ButtonGroup'

interface Props {
  bmConfigStore: BMConfigStore
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface IBirchmountLocationTable {
  key: number
  locationId: number
  locationName: string
  birchmountLocationId: number | JSX.Element | null
  options: JSX.Element
}

@inject('currentCompanyStore')
@observer
export class BirchmountLocationTable extends React.Component<Props & InjectedProps> {
  companyId = this.props.currentCompanyStore!.company!.id

  LOCATION_MESSAGE_KEY = 'locationMessage'

  MESSAGE_DURATION_IN_SEC = 2

  updateLocationId = async (locationWithMetaData: LocationMetaDataStore) => {
    try {
      locationWithMetaData.setIsUpdating(true)

      await locationWithMetaData.saveLocationMetaData(this.companyId)

      message.success({
        content: `Birchmount location ID updated for ${locationWithMetaData.locationName}`,
        key: this.LOCATION_MESSAGE_KEY,
        duration: this.MESSAGE_DURATION_IN_SEC,
      })
    } catch (error) {
      message.error({
        content: parseErrorMsg(
          error ||
            `An error occurred while updating Birchmount location ID for ${locationWithMetaData.locationName}`,
        ),
        key: this.LOCATION_MESSAGE_KEY,
        duration: this.MESSAGE_DURATION_IN_SEC,
      })
    } finally {
      locationWithMetaData.setIsUpdating(false)
    }
  }

  onSelectChange = async (
    locationWithMetaData: LocationMetaDataStore,
    locationId: string | null | undefined,
  ) => {
    const convertedLocationId =
      locationId === null || locationId === undefined ? null : parseInt(locationId)

    locationWithMetaData.setBirchmountLocationId(convertedLocationId)

    await this.updateLocationId(locationWithMetaData)
  }

  get columns(): Array<ColumnProps<IBirchmountLocationTable>> {
    return [
      {
        title: 'Location',
        dataIndex: 'locationName',
        width: 130,
      },
      {
        title: (
          <div>
            <div>Birchmount</div>
            <div>location ID</div>
          </div>
        ),
        dataIndex: 'birchmountLocationId',
      },
      {
        title: '',
        dataIndex: 'options',
        width: 85,
        fixed: 'right',
        align: 'center',
      },
    ]
  }

  get dataSource(): IBirchmountLocationTable[] {
    const birchmountLocations = this.props.bmConfigStore.locationContract
      ? this.props.bmConfigStore.locationContract.DATA
      : []

    return this.props.bmConfigStore.locationsWithMetaData.map((locationWithMetaData, index) => {
      return {
        key: index,
        locationId: locationWithMetaData.locationId,
        locationName: locationWithMetaData.locationName,
        birchmountLocationId: locationWithMetaData.isLocationIdEditable ? (
          <Select<string | undefined | null>
            allowClear
            dropdownMatchSelectWidth={false}
            style={{ width: 110 }}
            value={
              locationWithMetaData.birchmountLocationId == null
                ? undefined
                : locationWithMetaData.birchmountLocationId.toString()
            }
            onChange={async (locationId) => this.onSelectChange(locationWithMetaData, locationId)}
          >
            {birchmountLocations.map((location) => (
              <Select.Option key={location.LOCATION_ID}>
                {`${location.LOCATION_ID}: ${location.LOCATION_NAME}`}
              </Select.Option>
            ))}
          </Select>
        ) : (
          locationWithMetaData.birchmountLocationId
        ),
        options: (
          <ButtonGroup
            canSave={true}
            editable={locationWithMetaData.isLocationIdEditable}
            isUpdating={locationWithMetaData.isUpdating}
            onSave={async () => this.updateLocationId(locationWithMetaData)}
            onEdit={() => locationWithMetaData.setIsLocationIdEditable(true)}
            onCancel={() => {
              locationWithMetaData.revertBirchmountLocationId()
              locationWithMetaData.setIsLocationIdEditable(false)
            }}
          />
        ),
      }
    })
  }

  render() {
    return (
      <Table
        size='middle'
        pagination={false}
        columns={this.columns}
        dataSource={this.dataSource}
        rowClassName={() => 'editable-row'}
        scroll={{ x: true }}
      />
    )
  }
}
